import { useEffect, useRef, useState } from 'react';
import { DevicePhoneMobileIcon, ShieldCheckIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { api, ApiError } from '../lib/api.js';
import type {
  FieldAdministratorChange, FieldAdministratorDevice, FieldAdministratorView,
} from './recipientTypes.js';

const labels: Record<FieldAdministratorDevice['state'], string> = {
  approved: 'Approved recipient', not_approved: 'Needs device approval',
  keys_changed: 'Encryption keys changed. Approve again.', not_enrolled: 'Secure enrollment required',
  disabled: 'Device disabled', account_inactive: 'Account inactive',
  not_member: 'Add username to the group first', revoked: 'Approval revoked',
};

function explainFailure(cause: unknown): string {
  const reason = cause instanceof ApiError ? cause.reason : cause instanceof Error ? cause.message : '';
  if (reason === 'STEP_UP_REQUIRED' || reason === 'WEBAUTHN_REQUIRED') {
    return 'Security verification expired. Reopen this tab and verify with your security key.';
  }
  if (reason === 'field_administrator_configuration_changed') {
    return 'The group changed elsewhere. The latest configuration has been loaded. Review it before trying again.';
  }
  if (reason === 'field_administrator_keys_changed') {
    return 'This device changed its encryption keys. Review the refreshed fingerprint before approving.';
  }
  return 'The change could not be confirmed. Refresh the group and check the account and device status.';
}

type Pending = { change: Omit<Extract<FieldAdministratorChange, { action: 'approve_device' }>, 'revision'>
  | Omit<Extract<FieldAdministratorChange, { action: 'revoke_device' }>, 'revision'>
  | { action: 'remove_member'; userId: string }; label: string; username: string };

export function FieldAdministratorPanel() {
  const [view, setView] = useState<FieldAdministratorView | null>(null);
  const [query, setQuery] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const active = useRef(true);
  const changing = useRef(false);
  const generation = useRef(0);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; generation.current += 1; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const ticket = ++generation.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      void api.fieldAdministrators(query).then((data) => {
        if (!cancelled && active.current && ticket === generation.current) {
          setView(data);
          setLoading(false);
        }
      }).catch((cause) => {
        if (!cancelled && active.current && ticket === generation.current) {
          setError(explainFailure(cause));
          setView(null);
          setLoading(false);
        }
      });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query, refresh]);

  function review(next: Pending) {
    setPending(next);
    setConfirmed(false);
    setError(null);
    setNotice(null);
  }

  async function change(next: FieldAdministratorChange) {
    if (changing.current) return;
    changing.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    ++generation.current;
    try {
      await api.changeFieldAdministrator(next);
      if (!active.current) return;
      setPending(null);
      setConfirmed(false);
      setNotice('Configuration saved.');
    } catch (cause) {
      if (active.current) {
        setError(explainFailure(cause));
        setPending(null);
        setConfirmed(false);
      }
    } finally {
      changing.current = false;
      if (active.current) {
        setBusy(false);
        setLoading(true);
        setRefresh((value) => value + 1);
      }
    }
  }

  return <section aria-labelledby="field-administrator-title" className="max-w-5xl space-y-5">
    <div>
      <h2 id="field-administrator-title" className="text-h3 flex items-center gap-2">
        <UserGroupIcon className="h-6 w-6 text-accent-700" aria-hidden /> Field Administrator
      </h2>
      <p className="mt-2 text-sm text-neutral-700">
        Add a username, then approve each managed device that may receive wellness forms.
        This group does not grant system administrator permissions.
      </p>
    </div>
    <div className="card border-accent-300 bg-accent-100" role="status">
      <strong>{view ? `${view.approvedDeviceCount} approved recipient device${view.approvedDeviceCount === 1 ? '' : 's'}`
        : 'Checking recipient configuration'}</strong>
      <p className="mt-1 text-sm">
        {view?.recipientConfigurationReady ? 'Recipient devices are designated.'
          : 'No recipient is ready. Add a member and approve an enrolled device.'}
        {' '}Designation alone does not enable PQE2EE. The current form service can still decrypt records.
      </p>
    </div>
    <div className="flex flex-wrap items-end gap-3">
      <label className="field flex-1 min-w-[200px]" htmlFor="field-administrator-search">
        <span>Search username or name</span>
        <input id="field-administrator-search" type="search" className="input" value={query}
          maxLength={100} disabled={busy || Boolean(pending)}
          onChange={(event) => { setLoading(true); setQuery(event.target.value); }} />
      </label>
      <button type="button" className="btn btn-secondary" disabled={busy || loading || Boolean(pending)}
        onClick={() => { setLoading(true); setError(null); setRefresh((value) => value + 1); }}>Refresh</button>
    </div>
    {notice && <p role="status" className="text-sm">{notice}</p>}
    {error && <p role="alert" className="form-error">{error}</p>}
    {loading && <p role="status" className="text-sm text-neutral-700">Loading accounts and devices…</p>}
    {pending && view && <section className="card border-accent-500" aria-labelledby="recipient-confirm-title">
      <h3 id="recipient-confirm-title" className="text-h4">Review {pending.change.action === 'approve_device'
        ? 'device approval' : 'access removal'}</h3>
      <p className="mt-2 break-words">{pending.username} · {pending.label}</p>
      {pending.change.action === 'approve_device' ? <>
        <p className="mt-3 text-sm">Check that this enrolled device belongs to the intended recipient.
          The approval is bound to the fingerprint below. No other phone or computer gains access.</p>
        <p className="mt-2 break-all font-mono text-xs" aria-label="Encryption key fingerprint">
          {pending.change.fingerprint.match(/.{1,4}/gu)?.join(' ')}</p>
        <label className="mt-4 flex items-start gap-3 text-sm">
          <input type="checkbox" checked={confirmed} disabled={busy}
            onChange={(event) => setConfirmed(event.target.checked)} />
          I confirm this username and device should receive protected wellness forms.
        </label>
      </> : <p className="mt-3 text-sm">Future recipient selection will be blocked.
        Previously received records cannot be recalled from a device.
        {pending.change.action === 'remove_member' && ' Every device approval for this member will be revoked.'}</p>}
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" className="btn btn-primary"
          disabled={busy || loading || (pending.change.action === 'approve_device' && !confirmed)}
          onClick={() => void change({ ...pending.change, revision: view.revision })}>
          {busy ? 'Saving…' : pending.change.action === 'approve_device' ? 'Approve this device' : 'Confirm removal'}
        </button>
        <button type="button" className="btn btn-secondary" disabled={busy}
          onClick={() => setPending(null)}>Cancel</button>
      </div>
    </section>}
    {view?.users.length === 0 && !loading && <p>No matching accounts.</p>}
    {view?.truncated && <p className="text-sm">Showing the first 100 matches. Narrow your search to find another account.</p>}
    <ul className="space-y-4" aria-label="Field Administrator accounts">
      {view?.users.map((user) => <li key={user.id} className="card min-w-0">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div className="min-w-0">
            <h3 className="text-h4 break-words">{user.username || 'Username not set'}</h3>
            <p className="mt-1 text-sm text-neutral-700 break-words">{user.displayName} · {user.status}</p>
            <p className="mt-1 text-sm font-semibold">{user.member ? 'Group member' : 'Not a member'}</p>
          </div>
          <button type="button" className="btn btn-secondary"
            disabled={busy || loading || Boolean(pending) || (!user.member && user.status !== 'active')}
            onClick={() => user.member ? review({ change: { action: 'remove_member', userId: user.id },
              username: user.username || user.displayName, label: 'All assigned devices' })
              : void change({ action: 'add_member', userId: user.id, revision: view.revision })}>
            {user.member ? 'Remove member' : 'Add to group'}
          </button>
        </div>
        {user.devices.length === 0 && <p className="mt-4 text-sm text-neutral-700">
          No enrolled managed device is associated with this account. Sign in on the intended recipient device first.
        </p>}
        <ul className="mt-4 space-y-3" aria-label={`Devices for ${user.username || user.displayName}`}>
          {user.devices.map((device) => <li key={device.id} className="border-t border-neutral-300 pt-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-semibold break-words">
                  <DevicePhoneMobileIcon className="h-5 w-5 shrink-0" aria-hidden />{device.label}</p>
                <p className={`mt-1 text-sm ${device.eligible ? 'text-accent-700' : 'text-neutral-700'}`}>
                  {device.eligible && <ShieldCheckIcon className="inline h-4 w-4 mr-1" aria-hidden />}
                  {labels[device.state]}</p>
                <p className="mt-1 text-xs text-neutral-700 break-all">Device ID: {device.id}</p>
                <p className="mt-1 text-xs text-neutral-700">Last seen: {device.lastSeenAt
                  ? new Date(device.lastSeenAt).toLocaleString() : 'Not reported'}</p>
              </div>
              {['approved', 'keys_changed'].includes(device.state) && device.approvalId &&
                <button type="button" className="btn btn-secondary" disabled={busy || loading || Boolean(pending)}
                  onClick={() => review({ change: { action: 'revoke_device', userId: user.id,
                    deviceId: device.id, approvalId: device.approvalId! },
                  username: user.username || user.displayName, label: device.label })}>Revoke device</button>}
              {['not_approved', 'revoked', 'keys_changed'].includes(device.state) && device.fingerprint &&
                <button type="button" className="btn btn-secondary" disabled={busy || loading || Boolean(pending)}
                  onClick={() => review({ change: { action: 'approve_device', userId: user.id,
                    deviceId: device.id, fingerprint: device.fingerprint! },
                  username: user.username || user.displayName, label: device.label })}>Review device</button>}
            </div>
          </li>)}
        </ul>
      </li>)}
    </ul>
  </section>;
}
